// frontend/src/pages/Encuesta/definicionEncuesta.js
//
// Fuente única del contenido de la encuesta: los textos, las opciones y cómo se arma el payload.
//
// La usan las dos pantallas: el formulario público (EncuestaPublicaPage) para dibujar las preguntas
// y la de resultados (EncuestaResultadosPage) para ponerle nombre a lo que devuelve el backend. El
// backend responde códigos crudos ("p1", "MAS_FACIL", "NO_APLICA") justamente para que la redacción
// viva acá y en un solo lugar: si el estudio cambia cómo se lee una opción, el formulario y los
// resultados no pueden quedar diciendo cosas distintas.

// Mismo azul del navbar y del riel de navegación (ver NavegacionLateral.css), para que la
// encuesta se sienta parte del sistema y no una pantalla aparte con su propia paleta.
export const COLOR_PRIMARIO = '#2563eb';
export const COLOR_SECUNDARIO = '#3B82F6';

export const TITULO_ENCUESTA = 'Encuesta de satisfacción – GestiónEJP';

export const TEXTO_INTRODUCCION =
    'Queremos conocer tu experiencia con el sistema. ' +
    'Te lleva unos 3 minutos y nos sirve muchisimo. Las respuestas son anónimas.';

export const MENSAJE_CIERRE =
    '¡Gracias por responder!';

// Marca de "este navegador ya respondió". Lleva la versión adentro: cuando el estudio repita la
// encuesta como v2, la clave cambia y quien ya respondió la v1 puede responder la nueva.
export const CLAVE_RESPONDIDA = 'encuesta-satisfaccion-v1-respondida';

export const LARGO_MAXIMO_TEXTO = 1000;

/** Valor con el que viajan los "no aplica" de las preguntas 3 y 6. Igual que en el backend. */
export const NO_APLICA = 'NO_APLICA';

export const PREGUNTAS = [
    {
        codigo: 'p1',
        numero: 1,
        tipo: 'escala',
        obligatoria: true,
        enunciado: 'En general, ¿qué tan conforme estás con el sistema?',
        etiquetaMinima: 'Nada conforme',
        etiquetaMaxima: 'Muy conforme',
    },
    {
        codigo: 'p2',
        numero: 2,
        tipo: 'escala',
        obligatoria: true,
        enunciado: '¿Qué tan fácil te resulta usarlo en tu día a día?',
        etiquetaMinima: 'Muy difícil',
        etiquetaMaxima: 'Muy fácil',
    },
    {
        codigo: 'p3',
        numero: 3,
        tipo: 'escala',
        obligatoria: true,
        enunciado: '¿Qué tan fácil te resultó adaptarte cuando dejaron el Excel y el papel?',
        etiquetaMinima: 'Muy difícil',
        etiquetaMaxima: 'Muy fácil',
        etiquetaNoAplica: 'No trabajé con el método anterior',
    },
    {
        codigo: 'p4',
        numero: 4,
        tipo: 'opcion',
        obligatoria: true,
        enunciado: 'Comparado con antes, tu trabajo ahora es:',
        opciones: [
            { valor: 'MAS_DIFICIL', etiqueta: 'Más difícil' },
            { valor: 'IGUAL', etiqueta: 'Igual' },
            { valor: 'MAS_FACIL', etiqueta: 'Más fácil' },
        ],
    },
    {
        codigo: 'p5',
        numero: 5,
        tipo: 'opcion',
        obligatoria: true,
        enunciado: '¿Con qué frecuencia tenés errores o problemas con el sistema?',
        opciones: [
            { valor: 'NUNCA', etiqueta: 'Nunca' },
            { valor: 'POCAS_VECES', etiqueta: 'Pocas veces' },
            { valor: 'SEGUIDO', etiqueta: 'Seguido' },
        ],
    },
    {
        codigo: 'p6',
        numero: 6,
        tipo: 'escala',
        obligatoria: true,
        enunciado: 'Cuando necesitaste ayuda, ¿qué tan conforme quedaste con el soporte?',
        etiquetaMinima: 'Nada conforme',
        etiquetaMaxima: 'Muy conforme',
        etiquetaNoAplica: 'Nunca lo necesité',
    },
    {
        codigo: 'p7',
        numero: 7,
        tipo: 'opcion',
        obligatoria: true,
        enunciado: 'Si pudieras elegir, preferirías:',
        opciones: [
            { valor: 'SEGUIR', etiqueta: 'Seguir con el sistema' },
            { valor: 'ME_DA_IGUAL', etiqueta: 'Me da igual' },
            { valor: 'VOLVER_ANTES', etiqueta: 'Volver a trabajar como antes' },
        ],
    },
    {
        codigo: 'p8',
        numero: 8,
        tipo: 'texto',
        obligatoria: false,
        enunciado: '¿Qué es lo que más te sirve del sistema?',
        placeholder: 'Lo que no cambiarías por nada...',
    },
    {
        codigo: 'p9',
        numero: 9,
        tipo: 'texto',
        obligatoria: false,
        enunciado: '¿Qué mejorarías?',
        placeholder: 'Lo que te complica o te hace perder tiempo...',
    },
    {
        codigo: 'p10',
        numero: 10,
        tipo: 'texto',
        obligatoria: false,
        enunciado: '¿Algo más que quieras contarnos?',
        placeholder: 'Todo lo que quieras agregar...',
    },
];

/** Las preguntas que hay que responder sí o sí (1 a 7). */
export const PREGUNTAS_OBLIGATORIAS = PREGUNTAS.filter((p) => p.obligatoria);

/** Busca una pregunta por su código ("p1"). */
export const buscarPregunta = (codigo) => PREGUNTAS.find((p) => p.codigo === codigo);

/**
 * Texto que se muestra para un valor devuelto por el backend, en la pantalla de resultados.
 * En las escalas el valor es el número; en las de opción, el nombre del enum.
 */
export const etiquetaDeOpcion = (codigo, valor) => {
    const pregunta = buscarPregunta(codigo);
    if (!pregunta) return valor;
    if (valor === NO_APLICA) return pregunta.etiquetaNoAplica || 'No aplica';
    if (pregunta.tipo === 'opcion') {
        const opcion = (pregunta.opciones || []).find((o) => o.valor === valor);
        return opcion ? opcion.etiqueta : valor;
    }
    // Escala: el número solo, salvo en los extremos, donde se aclara qué significa.
    if (valor === '1') return `1 · ${pregunta.etiquetaMinima}`;
    if (valor === '5') return `5 · ${pregunta.etiquetaMaxima}`;
    return valor;
};

/** Enunciado corto para los títulos de la pantalla de resultados. */
export const enunciadoDe = (codigo) => {
    const pregunta = buscarPregunta(codigo);
    return pregunta ? `${pregunta.numero}. ${pregunta.enunciado}` : codigo;
};

/**
 * Pasa las respuestas del formulario al formato que espera el backend.
 *
 * En el estado del formulario una escala guarda el número o la cadena NO_APLICA; el backend recibe
 * el número en un campo y el "no aplica" en un booleano aparte. Esa separación es a propósito: deja
 * que el backend distinga "eligió no aplica" de "se saltó la pregunta", que si no se guardarían
 * igual (ver EncuestaService.resolverEscalaConNoAplica).
 */
export const construirPayload = (respuestas) => {
    const escala = (codigo) => (respuestas[codigo] === NO_APLICA ? null : respuestas[codigo] ?? null);
    const noAplica = (codigo) => respuestas[codigo] === NO_APLICA;
    const texto = (codigo) => {
        const valor = (respuestas[codigo] || '').trim();
        return valor === '' ? null : valor;
    };

    return {
        p1ConformidadGeneral: escala('p1'),
        p2FacilidadUso: escala('p2'),
        p3FacilidadAdaptacion: escala('p3'),
        p3NoAplica: noAplica('p3'),
        p4ComparacionTrabajo: respuestas.p4 || null,
        p5FrecuenciaProblemas: respuestas.p5 || null,
        p6ConformidadSoporte: escala('p6'),
        p6NoAplica: noAplica('p6'),
        p7Preferencia: respuestas.p7 || null,
        p8LoQueMasSirve: texto('p8'),
        p9QueMejoraria: texto('p9'),
        p10ComentarioLibre: texto('p10'),
    };
};
